import type {
  WebWidgetCallChannel,
  WebWidgetFacebookChannel,
  WebWidgetInstagramChannel,
  WebWidgetParsedCallChannel,
  WebWidgetParsedFacebookChannel,
  WebWidgetParsedInstagramChannel,
  WebWidgetParsedSmsChannel,
  WebWidgetParsedWhatsappChannel,
  WebWidgetSmsChannel,
  WebWidgetWhatsappChannel,
} from '@widgets/messenger/types'
import messengerCommonCastService from '@widgets/messenger/services/messengerCommonCastService'
import type {
  WebWidgetChatChannel,
  WebWidgetDetailsAppearance,
  WebWidgetDetailsChannelSorting,
  WebWidgetEmailChannel,
  WebWidgetParsedChatChannel,
  WebWidgetParsedEmailChannel,
} from '@/data/models/domain/messages/settings/webWidget/types'

// Only for the widget
export class MessengerWidgetCastService {
  public parseAppearance(rawAppearance: string): WebWidgetDetailsAppearance {
    return messengerCommonCastService.parseAppearance(rawAppearance)
  }

  public parseEmailChannel(rawEmailChannel: WebWidgetEmailChannel | null): WebWidgetParsedEmailChannel {
    return messengerCommonCastService.parseEmailChannel(rawEmailChannel)
  }

  public getDefaultSmsChannel(): WebWidgetParsedSmsChannel {
    return {
      isActive: false,
      uiSettings: messengerCommonCastService.getDefaultSmsChannelUiSettings(),
      suitableNumber: null,
    }
  }

  public parseSmsChannel(rawSmsChannel: WebWidgetSmsChannel | null): WebWidgetParsedSmsChannel {
    if (!rawSmsChannel) {
      return this.getDefaultSmsChannel()
    }

    return {
      ...rawSmsChannel,
      uiSettings: messengerCommonCastService.parseSmsChannelUiSettings(rawSmsChannel.uiSettings),
    } satisfies WebWidgetParsedSmsChannel
  }

  public getDefaultCallChannel(): WebWidgetParsedCallChannel {
    return {
      isActive: false,
      uiSettings: messengerCommonCastService.getDefaultCallChannelUiSettings(),
      suitableNumber: null,
    }
  }

  public parseCallChannel(rawCallChannel: WebWidgetCallChannel | null): WebWidgetParsedCallChannel {
    if (!rawCallChannel) {
      return this.getDefaultCallChannel()
    }

    return {
      ...rawCallChannel,
      uiSettings: messengerCommonCastService.parseCallChannelUiSettings(rawCallChannel.uiSettings),
    }
  }

  public getDefaultWhatsappChannel(): WebWidgetParsedWhatsappChannel {
    return {
      isActive: false,
      uiSettings: messengerCommonCastService.getDefaultWhatsappChannelUiSettings(),
      suitableNumber: null,
    }
  }

  public parseWhatsappChannel(rawWhatsappChannel: WebWidgetWhatsappChannel | null): WebWidgetParsedWhatsappChannel {
    if (!rawWhatsappChannel) {
      return this.getDefaultWhatsappChannel()
    }

    return {
      ...rawWhatsappChannel,
      uiSettings: messengerCommonCastService.parseWhatsappChannelUiSettings(rawWhatsappChannel.uiSettings),
    }
  }

  public getDefaultFacebookChannel(): WebWidgetParsedFacebookChannel {
    return {
      isActive: false,
      uiSettings: messengerCommonCastService.getDefaultFacebookChannelUiSettings(),
      facebookChatUrl: '',
    }
  }

  public parseFacebookChannel(rawFacebookChannel: WebWidgetFacebookChannel | null): WebWidgetParsedFacebookChannel {
    if (!rawFacebookChannel) {
      return this.getDefaultFacebookChannel()
    }

    return {
      ...rawFacebookChannel,
      uiSettings: messengerCommonCastService.parseFacebookChannelUiSettings(rawFacebookChannel.uiSettings),
    }
  }

  public getDefaultInstagramChannel(): WebWidgetParsedInstagramChannel {
    return {
      isActive: false,
      uiSettings: messengerCommonCastService.getDefaultInstagramChannelUiSettings(),
      instagramChatUrl: '',
    }
  }

  public parseInstagramChannel(rawInstagramChannel: WebWidgetInstagramChannel | null): WebWidgetParsedInstagramChannel {
    if (!rawInstagramChannel) {
      return this.getDefaultInstagramChannel()
    }

    return {
      ...rawInstagramChannel,
      uiSettings: messengerCommonCastService.parseInstagramChannelUiSettings(rawInstagramChannel.uiSettings),
    }
  }

  public parseChatChannel(rawChatChannel: WebWidgetChatChannel | null): WebWidgetParsedChatChannel {
    return messengerCommonCastService.parseChatChannel(rawChatChannel)
  }

  public parseChannelSorting(rawChannelSorting: WebWidgetDetailsChannelSorting): WebWidgetDetailsChannelSorting {
    return messengerCommonCastService.parseChannelSorting(rawChannelSorting)
  }
}

export default new MessengerWidgetCastService()
